import { RefreshCw, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Notice } from "@/components/Notice";
import { Spinner } from "@/components/ui/spinner";
import { formatDateTime } from "@/utils/dates";
import type { HealthReport } from "@/features/system/api/system.api";
import blogicLogo from "@/assets/blogic-logo.png";

export default function MaintenancePage({
  report = null,
  checkedAt = null,
  busy = false,
  onRetry,
}: {
  report?: HealthReport | null;
  checkedAt?: number | null;
  busy?: boolean;
  onRetry?: () => void;
}) {
  const retry = onRetry ?? (() => window.location.reload());
  const endsAt = report?.estimatedEndsAt ? formatDateTime(report.estimatedEndsAt) : "";

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-muted px-4 py-10">
      <Card className="w-full max-w-md">
        <CardContent className="p-8">
          <div className="flex flex-col items-center text-center">
            <img src={blogicLogo} alt="BLogic Systems" className="h-12 w-auto" />

            <span className="mt-7 flex size-14 items-center justify-center rounded-full bg-amber-100 text-amber-700">
              <Wrench className="size-7" aria-hidden="true" />
            </span>

            <h1 className="mt-5 text-xl font-semibold text-slate-900">
              We&apos;re down for maintenance
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              The Merchant Portal is temporarily unavailable while we finish some work on
              our systems. Your data is safe — please check back in a few minutes.
            </p>
          </div>

          {report?.message && (
            <Notice tone="warn" className="mt-5 text-left">
              {report.message}
            </Notice>
          )}

          {endsAt && (
            <p className="mt-5 text-center text-sm text-slate-900">
              Estimated to be back by <span className="font-semibold">{endsAt}</span>
            </p>
          )}

          <Button variant="outline" className="mt-6 w-full" onClick={retry} disabled={busy}>
            {busy ? (
              <Spinner className="mr-2" aria-hidden="true" />
            ) : (
              <RefreshCw className="mr-2 size-4" aria-hidden="true" />
            )}
            {busy ? "Checking…" : "Try again"}
          </Button>

          <p className="mt-4 text-center text-xs text-muted-foreground" aria-live="polite">
            This page checks automatically and reloads itself once the service is back
            {checkedAt ? ` · last checked ${new Date(checkedAt).toLocaleTimeString()}` : ""}.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
